import { observer } from 'mobx-react-lite'

import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Popover from '@mui/material/Popover'

import { Text } from '@mxenabled/mxui'
import { CheckCircle as SelectedIcon } from '@mxenabled/mx-icons'

import { useGlobalCopyStore, useEvent, ANALYTICS_EVENTS } from '../../../common'
import { useDebtsUiStore } from '../../hooks/useDebtsUiStore'
import { DebtPriority } from '../../../core/constants/Debts'

interface DebtsPriorityPopoverProps {
  buttonEl: HTMLButtonElement | null
  onClose: () => void
}

export const DebtsPriorityPopover = observer(({ buttonEl, onClose }: DebtsPriorityPopoverProps) => {
  const { onEvent } = useEvent()
  const { debts: copy } = useGlobalCopyStore()
  const { selectedDebtPriority, setSelectedDebtPriority } = useDebtsUiStore()

  const isOpen = Boolean(buttonEl)

  const options = [
    {
      priority: DebtPriority.FASTEST_PAYOFF_FIRST,
      text: copy.priority_sort_fastest_payoff,
    },
    {
      priority: DebtPriority.HIGHEST_INTEREST,
      text: copy.priority_sort_highest_interest,
    },
    {
      priority: DebtPriority.LOWEST_BALANCE,
      text: copy.priority_sort_lowest_balance,
    },
    {
      priority: DebtPriority.HIGHEST_BALANCE,
      text: copy.priority_sort_highest_balance,
    },
  ]

  const handleClick = (priority: DebtPriority) => {
    setSelectedDebtPriority(priority)
    onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_PRIORITY, { debts_priority: priority })
  }

  return (
    <Popover
      anchorEl={buttonEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      id="prioritize-debts"
      onClose={onClose}
      open={isOpen}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <List>
        {options.map(({ priority, text }) => (
          <ListItem
            key={priority}
            onClick={() => handleClick(priority)}
            sx={{
              bgcolor: selectedDebtPriority === priority ? 'primary.main' : undefined,
              color: selectedDebtPriority === priority ? '#fff' : undefined,
              justifyContent: 'space-between',
              minHeight: 44,
              minWidth: 268,
              px: 24,
              py: 12,
              '&:hover': {
                bgcolor: 'background.highlight',
                cursor: 'pointer',
                color: 'text.primary',
              },
            }}
          >
            <Text color="inherit" variant="body2">
              {text}
            </Text>
            {selectedDebtPriority === priority && (
              <SelectedIcon color="inherit" filled={true} fontSize="small" />
            )}
          </ListItem>
        ))}
      </List>
    </Popover>
  )
})
